import { Routes, Route, Navigate, useNavigate } from "react-router";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./auth";
import { AppDataProvider } from "./hooks/useAppData";
import { roleSlug } from "./auth/roleMap";
import { BootScreen } from "./layout";
import { RoleSwitcher } from "./screens/RoleSwitcher";
import { RoleDashboard } from "./screens/RoleDashboard";
import { ErrorScreen, NoAccessScreen } from "./screens/ErrorScreen";
import { PreviewScreen } from "./screens/PreviewScreen";
import { AppUpdate } from "./components/shared/AppUpdate";

/**
 * Top-level boot flow (B.1).
 *
 *   /             → AuthGate: BootScreen → RoleSwitcher → /{role}
 *   /:role        → RoleDashboard (validates slug ↔ availableRoles)
 *   /preview      → PreviewScreen (Stage 1b/1c smoke-test, kept as a sandbox)
 *   anything else → /
 */
export function App() {
  return (
    <AuthProvider>
      <AppDataProvider>
        <Routes>
          <Route path="/" element={<AuthGate />} />
          <Route path="/:roleSlug/*" element={<ProtectedRoleRoute />} />
          <Route path="/preview" element={<PreviewScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AppUpdate />
      </AppDataProvider>
    </AuthProvider>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * AuthGate at "/" — decides what to show based on auth state.
 * ────────────────────────────────────────────────────────────────────── */

function AuthGate() {
  const { status, availableRoles, activeRole, error, errorStatus, refresh, signOut } = useAuth();
  const navigate = useNavigate();

  // If the user has exactly one role OR has an active role saved, auto-route.
  useEffect(() => {
    if (status !== "authed") return;
    const target = activeRole ?? (availableRoles.length === 1 ? availableRoles[0]! : null);
    if (target) navigate(`/${roleSlug(target)}`, { replace: true });
  }, [status, availableRoles, activeRole, navigate]);

  if (status === "loading") return <BootScreen status="Авторизация…" />;

  if (status === "error") {
    if (errorStatus === 403) return <NoAccessScreen onRetry={refresh} onSignOut={signOut} />;
    return (
      <ErrorScreen
        title="НЕТ ПОДКЛЮЧЕНИЯ"
        code={`ERR · AUTH · ${error ?? "UNKNOWN"}`}
        detail="Не удалось получить данные пользователя. Проверьте связь и повторите."
        onRetry={refresh}
        onSignOut={signOut}
      />
    );
  }

  // status === "authed"
  if (availableRoles.length === 0) {
    return (
      <ErrorScreen
        title="НЕТ ДОСТУПА"
        code="ERR · NO_ROLE · 403"
        detail="Аккаунт не привязан к UniBooking — нет активных ролей. Обратись к владельцу сети для назначения роли."
        onRetry={refresh}
        onSignOut={signOut}
      />
    );
  }

  // Multi-role and no active pick yet → role-switcher
  return <RoleSwitcher />;
}

/* ────────────────────────────────────────────────────────────────────────
 * Protected route for /:roleSlug/*.
 * Ensures auth is loaded before rendering the dashboard.
 * ────────────────────────────────────────────────────────────────────── */

function ProtectedRoleRoute() {
  const { status, error, errorStatus, refresh, signOut } = useAuth();
  if (status === "loading") return <BootScreen status="Авторизация…" />;
  if (status === "error") {
    if (errorStatus === 403) return <NoAccessScreen onRetry={refresh} onSignOut={signOut} />;
    return (
      <ErrorScreen
        title="НЕТ ПОДКЛЮЧЕНИЯ"
        code={`ERR · AUTH · ${error ?? "UNKNOWN"}`}
        detail="Не удалось получить данные пользователя."
        onRetry={refresh}
        onSignOut={signOut}
      />
    );
  }
  return <RoleDashboard />;
}
